import { useState } from "react";
import clsx from "clsx";
import { Input } from "@/components/ui/input";
import type { SpecValue, SpecValueInputs } from "@/types/specList";

export interface SelectedSpecValue extends SpecValue {
  selected: boolean;
  customValues?: Partial<SpecValue>;
}

const getCurrentValue = (
  specValue: SelectedSpecValue,
  field: keyof SpecValue,
  fallback = "",
): string => {
  const customValue = specValue?.customValues?.[field];
  const masterValue = specValue?.[field];

  if (customValue || customValue === 0) return customValue.toString();
  if (masterValue || masterValue === 0) return masterValue.toString();
  return fallback;
};

const isValueCustomized = (specValue: SelectedSpecValue, field: keyof SpecValue): boolean => {
  const customValue = specValue?.customValues?.[field];
  const masterValue = specValue?.[field];

  return customValue !== undefined && customValue !== masterValue;
};

const SpecValueCell = (
  specValue: SelectedSpecValue,
  field: keyof SpecValue,
  handleValueChange: (specId: string, field: keyof SpecValue, value: string) => void,
) => {
  const [value, setValue] = useState<string>(() => getCurrentValue(specValue, field));

  const isCustomized = isValueCustomized(specValue, field);

  return (
    <>
      {specValue.selected ? (
        <Input
          type="number"
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
          className={clsx("w-20 h-8", {
            "bg-blue-50 border-blue-300 text-blue-700 font-medium": isCustomized,
          })}
          placeholder="—"
          onBlur={() => handleValueChange(specValue.id, field, value)}
        />
      ) : (
        getCurrentValue(specValue, field, "-")
      )}
    </>
  );
};

// Only access previousValues for numeric fields that exist in SpecValueInputs
const numericFields: (keyof SpecValueInputs)[] = [
  "lsl",
  "tgt",
  "usl",
  "jmpYmin",
  "jmpYmax",
  "jmpYinc",
  "libMedian",
  "libMean",
  "libStd",
  "libQ90",
  "libQ10",
  "libN",
];

export const SpecValueCellWithMasterValue = (
  specValue: SelectedSpecValue,
  field: keyof SpecValue,
  handleValueChange: (specId: string, field: keyof SpecValue, value: string) => void,
) => {
  const [currentValue, setCurrentValue] = useState<string>(() => getCurrentValue(specValue, field));

  const previousValue = numericFields.includes(field as keyof SpecValueInputs)
    ? specValue.previousValues?.[field as keyof SpecValueInputs]
    : undefined;
  const hasChangedFromMaster =
    previousValue !== undefined && previousValue !== null && currentValue !== String(previousValue);

  const isCustomized = isValueCustomized(specValue, field);

  return (
    <>
      {specValue.selected ? (
        <Input
          type="number"
          step="0.1"
          value={currentValue}
          onChange={(e) => setCurrentValue(e.currentTarget.value)}
          className={clsx("w-20 h-8", {
            "bg-blue-50 border-blue-300 text-blue-700 font-medium":
              isCustomized || hasChangedFromMaster,
          })}
          placeholder="—"
          onBlur={() => handleValueChange(specValue.id, field, currentValue)}
        />
      ) : (
        <div className="flex items-center gap-2">
          <span className={clsx({ "text-blue-600 font-medium": hasChangedFromMaster })}>
            {currentValue || "-"}
          </span>
          {hasChangedFromMaster && previousValue !== undefined && (
            <span className="text-xs text-muted-foreground/60">(mst: {previousValue})</span>
          )}
        </div>
      )}
    </>
  );
};

export default SpecValueCell;
